/**
 * AP-37 Detailblatt N 4.2 — **Rechenweg**: eine kleine Rechensprache, damit eine
 * abgeleitete Zahl nie von der KI gerechnet wird, sondern vom Server (KI-2).
 *
 * Grammatik (fest, kein `eval`):
 *
 *   ausdruck := summe
 *   summe    := produkt (('+' | '-') produkt)*
 *   produkt  := vorzeichen (('*' | '×' | '/' | '÷') vorzeichen)*
 *   vorzeichen := ('-' | '+')? faktor
 *   faktor   := zahl | name | funktion '(' argumente ')' | '(' summe ')'
 *   funktion := aufrunden | abrunden | runden | min | max
 *
 * Zahlen mit Punkt oder Komma als Dezimaltrenner (`1,20`) — Argumente einer Funktion
 * deshalb mit Komma **und Leerzeichen** oder mit Strichpunkt trennen (`runden(x, 2)`,
 * `max(a;b)`); `max(1,2)` ist die Zahl 1,2. Namen aus Buchstaben,
 * Ziffern und `_` (Umlaute erlaubt). Einheiten stehen an den Größen und werden
 * mitgeführt, nicht gerechnet.
 */

export type RechenwegQuellenArt = 'LV' | 'DATEI' | 'PLAN' | 'ERFAHRUNG' | 'RICHTWERT' | 'ARTIKEL' | 'BEFUND' | 'EINGABE'

export const RECHENWEG_QUELLEN: readonly RechenwegQuellenArt[] = ['LV', 'DATEI', 'PLAN', 'ERFAHRUNG', 'RICHTWERT', 'ARTIKEL', 'BEFUND', 'EINGABE']

export interface RechenwegQuelle {
  art: RechenwegQuellenArt
  /** LV: OZ */
  oz?: string
  /** DATEI/PLAN: Datei der Kalkulation, Seite (1-basiert), Rechteck in PDF-Punkten */
  fileId?: string
  seite?: number
  rect?: [number, number, number, number]
  /** ERFAHRUNG/RICHTWERT/ARTIKEL/BEFUND: Kennung des Datensatzes und sein Name */
  id?: string
  name?: string
  /** ARTIKEL: Feld (z. B. `weightPerUnit`) */
  feld?: string
  /** ERFAHRUNG: Zahl der Treffer */
  treffer?: number
  zitat?: string
}

export interface RechenwegGroesse {
  wert: number
  einheit?: string
  quelle: RechenwegQuelle
}

export interface Rechenweg {
  ausdruck: string
  begruendung?: string
  groessen: Record<string, RechenwegGroesse>
  /** `TOTAL` = für die ganze Position, `PER_UNIT` = je Positionseinheit */
  bezug: 'TOTAL' | 'PER_UNIT'
  /** vom Server gerechnet — nie aus der Eingabe übernommen */
  ergebnis?: number
  /** Die Zahl an der Zeile wurde danach von Hand geändert (9.). */
  geaendert?: boolean
}

export class RechenwegFehler extends Error {
  constructor(
    readonly code: 'SYNTAX' | 'NAME_UNBEKANNT' | 'FUNKTION_UNBEKANNT' | 'DIVISION_NULL' | 'NICHT_ENDLICH' | 'QUELLE_FEHLT',
    message: string,
  ) {
    super(message)
    this.name = 'RechenwegFehler'
  }
}

type Token = { t: 'zahl'; v: number } | { t: 'name'; v: string } | { t: 'op'; v: string }

const FUNKTIONEN: Record<string, { stellen: [number, number]; f: (a: number[]) => number }> = {
  aufrunden: { stellen: [1, 1], f: ([x]) => Math.ceil(x - 1e-9) },
  abrunden: { stellen: [1, 1], f: ([x]) => Math.floor(x + 1e-9) },
  runden: {
    stellen: [1, 2],
    f: ([x, n = 0]) => {
      const s = 10 ** Math.max(0, Math.min(6, Math.trunc(n)))
      return (Math.sign(x) * Math.round(Math.abs(x) * s + 1e-7)) / s
    },
  },
  min: { stellen: [1, 20], f: (a) => Math.min(...a) },
  max: { stellen: [1, 20], f: (a) => Math.max(...a) },
}

function zerlege(ausdruck: string): Token[] {
  const aus: Token[] = []
  let i = 0
  const s = ausdruck
  if (s.length > 500) throw new RechenwegFehler('SYNTAX', 'Ausdruck zu lang (höchstens 500 Zeichen).')
  while (i < s.length) {
    const c = s[i]
    if (/\s/.test(c)) {
      i++
      continue
    }
    if (/[0-9]/.test(c)) {
      const m = /^[0-9]+(?:[.,][0-9]+)?/.exec(s.slice(i))!
      aus.push({ t: 'zahl', v: Number(m[0].replace(',', '.')) })
      i += m[0].length
      continue
    }
    if (/[A-Za-zÄÖÜäöüß_]/.test(c)) {
      const m = /^[A-Za-zÄÖÜäöüß_][A-Za-zÄÖÜäöüß0-9_]*/.exec(s.slice(i))!
      aus.push({ t: 'name', v: m[0] })
      i += m[0].length
      continue
    }
    if ('+-*/×÷(),;−·:'.includes(c)) {
      // Typografische Zeichen wie im Druck: − Minus, · und × mal, : und ÷ geteilt.
      const op = c === '−' ? '-' : c === '·' || c === '×' ? '*' : c === ':' || c === '÷' ? '/' : c === ';' ? ',' : c
      aus.push({ t: 'op', v: op })
      i++
      continue
    }
    throw new RechenwegFehler('SYNTAX', `Unerwartetes Zeichen „${c}" an Stelle ${i + 1}.`)
  }
  return aus
}

/** Namen, die ein Ausdruck benutzt (ohne Funktionen) — für die Prüfung, ob jede Größe eine Quelle hat. */
export function namenImAusdruck(ausdruck: string): string[] {
  const t = zerlege(ausdruck)
  const namen = new Set<string>()
  t.forEach((x, i) => {
    if (x.t === 'name' && !(t[i + 1]?.t === 'op' && t[i + 1].v === '(' && FUNKTIONEN[x.v.toLowerCase()])) namen.add(x.v)
  })
  return [...namen]
}

/** Rechnet einen Ausdruck mit benannten Werten. Wirft `RechenwegFehler`, nie etwas anderes. */
export function rechne(ausdruck: string, werte: Readonly<Record<string, number>>): number {
  const t = zerlege(ausdruck)
  let i = 0
  const naechstes = () => t[i]
  const erwarte = (v: string) => {
    const x = t[i]
    if (!x || x.t !== 'op' || x.v !== v) throw new RechenwegFehler('SYNTAX', `„${v}" erwartet.`)
    i++
  }
  const summe = (): number => {
    let a = produkt()
    for (let x = naechstes(); x && x.t === 'op' && (x.v === '+' || x.v === '-'); x = naechstes()) {
      i++
      const b = produkt()
      a = x.v === '+' ? a + b : a - b
    }
    return a
  }
  const produkt = (): number => {
    let a = vorzeichen()
    for (let x = naechstes(); x && x.t === 'op' && (x.v === '*' || x.v === '/'); x = naechstes()) {
      i++
      const b = vorzeichen()
      if (x.v === '/') {
        if (Math.abs(b) < 1e-12) throw new RechenwegFehler('DIVISION_NULL', 'Division durch 0.')
        a = a / b
      } else a = a * b
    }
    return a
  }
  const vorzeichen = (): number => {
    const x = naechstes()
    if (x && x.t === 'op' && (x.v === '-' || x.v === '+')) {
      i++
      const w = vorzeichen()
      return x.v === '-' ? -w : w
    }
    return faktor()
  }
  const faktor = (): number => {
    const x = naechstes()
    if (!x) throw new RechenwegFehler('SYNTAX', 'Ausdruck endet unerwartet.')
    if (x.t === 'zahl') {
      i++
      return x.v
    }
    if (x.t === 'op' && x.v === '(') {
      i++
      const w = summe()
      erwarte(')')
      return w
    }
    if (x.t === 'name') {
      i++
      const n = naechstes()
      if (n && n.t === 'op' && n.v === '(') {
        const fn = FUNKTIONEN[x.v.toLowerCase()]
        if (!fn) throw new RechenwegFehler('FUNKTION_UNBEKANNT', `Unbekannte Funktion „${x.v}".`)
        i++
        const args: number[] = [summe()]
        while (naechstes()?.t === 'op' && naechstes()!.v === ',') {
          i++
          args.push(summe())
        }
        erwarte(')')
        if (args.length < fn.stellen[0] || args.length > fn.stellen[1]) throw new RechenwegFehler('SYNTAX', `„${x.v}" mit ${args.length} Werten.`)
        return fn.f(args)
      }
      if (!Object.prototype.hasOwnProperty.call(werte, x.v)) throw new RechenwegFehler('NAME_UNBEKANNT', `Unbekannte Größe „${x.v}".`)
      return werte[x.v]
    }
    throw new RechenwegFehler('SYNTAX', `Unerwartetes „${x.v}".`)
  }
  if (!t.length) throw new RechenwegFehler('SYNTAX', 'Leerer Ausdruck.')
  const ergebnis = summe()
  if (i < t.length) throw new RechenwegFehler('SYNTAX', `Unerwartetes „${String(t[i].v)}".`)
  if (!Number.isFinite(ergebnis)) throw new RechenwegFehler('NICHT_ENDLICH', 'Ergebnis ist keine endliche Zahl.')
  return ergebnis
}

/**
 * Einen Rechenweg prüfen und rechnen (4.2): jede benutzte Größe muss einen Wert und
 * eine Quelle haben; das Ergebnis rechnet der Server, eine mitgelieferte Zahl zählt nicht.
 * Gerundet auf sechs Stellen wie die Stunden im Blatt.
 */
export function rechneRechenweg(r: Pick<Rechenweg, 'ausdruck' | 'groessen'>): number {
  const namen = namenImAusdruck(r.ausdruck)
  const werte: Record<string, number> = {}
  for (const n of namen) {
    const g = r.groessen?.[n]
    if (!g || typeof g.wert !== 'number' || !Number.isFinite(g.wert)) throw new RechenwegFehler('NAME_UNBEKANNT', `Größe „${n}" ohne Wert.`)
    if (!g.quelle || !RECHENWEG_QUELLEN.includes(g.quelle.art)) throw new RechenwegFehler('QUELLE_FEHLT', `Größe „${n}" ohne Quelle.`)
    werte[n] = g.wert
  }
  return Math.round(rechne(r.ausdruck, werte) * 1e6) / 1e6
}

/** „12 Stk × 0,25 h/Stk = 3,00 h" — die Größen eingesetzt, für die Anzeige. */
export function rechenwegText(r: Rechenweg, einheitErgebnis = ''): string {
  const zahl = (x: number) => x.toLocaleString('de-DE', { maximumFractionDigits: 4 })
  // Erst die Zeichen des Ausdrucks, dann die Größen — sonst würde aus „h/Stk" „h÷Stk".
  let text = r.ausdruck.replace(/\*/g, '×').replace(/\//g, '÷')
  for (const [n, g] of Object.entries(r.groessen ?? {}).sort((a, b) => b[0].length - a[0].length)) {
    text = text.replace(new RegExp(`(?<![A-Za-zÄÖÜäöüß0-9_])${n}(?![A-Za-zÄÖÜäöüß0-9_(])`, 'g'), `${zahl(g.wert)}${g.einheit ? ` ${g.einheit}` : ''}`)
  }
  return r.ergebnis === undefined ? text : `${text} = ${zahl(r.ergebnis)}${einheitErgebnis ? ` ${einheitErgebnis}` : ''}`
}
